import { useParams } from "react-router";
import { useState, useEffect } from "react";
import Seo from "./Seo";
import { findCuratedWallet, BALANCES_CHECKED } from "./curatedWalletData";
import { idShapeOf, verifyId } from "./btcId";
import NotFound from "./NotFound";
import { walletMeta, lookupMeta } from "./routeMeta";
import { SANCTIONS_SOURCE, SANCTIONS_AS_OF } from "./sanctionsSummary";
import {
  findMiningPool,
  MINING_POOLS_SAMPLE,
  MINING_POOLS_AS_OF,
} from "./miningPools";
import styled from "styled-components";
import LoadingBar from "./LoadingBar";
import FullTxDetails from "./FullTxDetails";
import { fmtUsd, satsToBtc, truncateHash, btcToString } from "./utils";
import { counterpartiesOf } from "./txCounterparties";
import { getBtcPrice } from "./getBtcPrice";
import { lookupUrl, takeEmbeddedLookup } from "./embeddedLookup";
import {
  BtcBalPara,
  UsdBalPara,
  LoadingWrapper,
  PageContainer,
} from "./sharedStyleds";
import { Link } from "react-router";

const AddressList = styled.ul`
  margin-top: 5px;
  margin-bottom: 5px;
  font-size: 12px;
`;

const TxLink = styled(Link)`
  color: #003f7b;
`;

const BtcExplorerPage = PageContainer;

const TerminalCard = styled.div`
  padding: 0 1rem;
  width: auto;
`;

const Title = styled.h1`
  /* h1 for semantics, sized to the h2 it replaces so nothing moves */
  font-size: 1.5rem;
  line-height: 1.4em;
  margin-top: 0;
  margin-bottom: 0;
`;

// A lookup that found nothing is not an error, and a bare "No results" left it
// ambiguous whether the address was wrong, empty, or the explorer was down.
const Empty = styled.p`
  max-width: 75ch;
  color: #a3abba;
`;

const BalanceHeader = styled.h3`
  margin-bottom: 0.5rem;
`;

const TxDetails = styled.p`
  margin-bottom: 0.2rem;
`;

// The other side of a transaction. Muted, because the amount is what the row is
// read for and this answers the question after it.
const Counterparty = styled.p`
  margin-bottom: 0.2rem;
  font-size: 0.85rem;
  color: #a3abba;
`;

// Same blue as the address links on the transaction page and the sanctions
// list, so a followable address looks the same wherever it appears.
const CounterpartyLink = styled(Link)`
  color: #62c4ff;
`;

// Same tag as Bitcoin Live and the transaction page. A counterparty that is one
// of the notable wallets is the most interesting thing a row can say, and it is
// the whole reason the directory and the explorer are the same site.
const CounterpartyTag = styled(Link)`
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0 0.3rem;
  border: 1px solid #32a1ce;
  border-radius: 3px;
  font-size: 0.7rem;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background-color: #32a1ce;
    color: #222225;
  }
`;

// How many counterparties a row names before it stops. A batch payout can have
// hundreds of recipients and the row is a summary, not the transaction page -
// which is one click away and lists every one of them.
const COUNTERPARTIES_SHOWN = 2;

// The row under the amount: who paid, or who was paid. Renders nothing at all
// when there is nothing it can honestly say.
const Counterparties = ({ tx, own, incoming }) => {
  const other = counterpartiesOf(tx, own);

  if (other.kind === "unknown") return null;
  if (other.kind === "coinbase") return <Counterparty>newly mined</Counterparty>;
  if (other.kind === "self") {
    return (
      <Counterparty>{incoming ? "from itself" : "to itself"}</Counterparty>
    );
  }

  const shown = other.addresses.slice(0, COUNTERPARTIES_SHOWN);
  const rest = other.addresses.length - shown.length;

  return (
    <Counterparty>
      {incoming ? "from" : "to"}{" "}
      {shown.map((address, i) => {
        const curated = findCuratedWallet(address);
        return (
          <span key={address}>
            {i > 0 && ", "}
            <CounterpartyLink to={`/btc-explorer/${address}`}>
              {truncateHash(address)}
            </CounterpartyLink>
            {curated && (
              <CounterpartyTag to={`/btc-explorer/${address}`}>
                {curated.label}
              </CounterpartyTag>
            )}
          </span>
        );
      })}
      {rest > 0 && ` and ${rest.toLocaleString()} more`}
    </Counterparty>
  );
};

// A known wallet says whose it is. The label was already looked up for the page
// title and the link preview, so the page was the only place that did not say
// what you were looking at.
const KnownWallet = styled.section`
  border: 1px solid #3f3f44;
  border-left: 3px solid #32a1ce;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
  max-width: 75ch;
`;

const KnownCategory = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #a3abba;
`;

const KnownNote = styled.p`
  margin: 0.35rem 0 0.4rem 0;
  font-size: 0.85rem;
`;

// The recorded balance and the way back to the list. A bech32 address is 42
// characters without a break in it, so it has to be allowed to wrap.
const KnownMeta = styled.p`
  margin: 0 0 0.4rem 0;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
`;

// The same caveat the notable wallets list carries. A label is a widely held
// attribution, not something anyone proved by signing a message, and the page
// should not imply otherwise just because it is showing one wallet.
const KnownCaveat = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #a3abba;
`;

// A sanctions match is a different kind of statement from a curated label, so
// it does not look like one. The curated block says "widely believed"; this one
// reports a published act of the US government, and carries the source and the
// snapshot date so the reader can check it and can see how old it is.
const Sanctioned = styled.section`
  border: 1px solid #ff3c74;
  border-left: 3px solid #ff3c74;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
  max-width: 75ch;
`;

const SanctionedLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ff3c74;
`;

const SanctionedName = styled.h2`
  margin: 0.1rem 0 0.2rem;
  font-size: 1.05rem;
`;

const SanctionedMeta = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #a3abba;
`;

// A third kind of claim, and the strongest of them. The curated label says
// "widely believed"; the sanctions banner reports a government designation; this
// one is arithmetic on public blocks.
const PoolBanner = styled.section`
  border: 1px solid #3f3f44;
  border-left: 3px solid #f5a623;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
  max-width: 75ch;
`;

const PoolLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f5a623;
`;

const PoolName = styled.h2`
  margin: 0.1rem 0 0.2rem;
  font-size: 1.05rem;
`;

const PoolMeta = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #a3abba;
`;

const BlockRef = styled.span`
  color: #a3abba;
`;

const getWalletData = async (walletId) => {
  const embedded = takeEmbeddedLookup(walletId);
  if (embedded) return { ...embedded, fetched: true };
  try {
    const response = await fetch(lookupUrl("wallet", walletId));
    if (!response.ok) {
      return { error: "No results", wallet: [], fetched: true };
    }
    return { ...(await response.json()), fetched: true };
  } catch (error) {
    console.error("Wallet lookup failed:", error);
    return { error: "Could not reach the block explorer", wallet: [], fetched: true };
  }
};

const getTxData = async (txHash) => {
  const embedded = takeEmbeddedLookup(txHash);
  if (embedded) return { ...embedded, fetched: true };
  try {
    const response = await fetch(lookupUrl("transaction", txHash));
    if (!response.ok) {
      return { error: "No results", wallet: [], fetched: true };
    }
    return { ...(await response.json()), fetched: true };
  } catch (error) {
    console.error("Transaction lookup failed:", error);
    return { error: "Could not reach the block explorer", wallet: [], fetched: true };
  }
};

const BtcExplorer = () => {
  let { walletId } = useParams();
  // "transaction", "wallet", or null for an id that is not either - the wrong
  // shape entirely, or an address whose own checksum says it has a typo in it.
  // Netlify has already answered 404 for these; nothing below should look one
  // up, and until this existed the page asked blockchain.info anyway and showed
  // the 400 that came back as "No results".
  const kind = verifyId(walletId);
  const isTx = kind === "transaction";
  const [isLoading, setIsLoading] = useState(true);
  const [walletData, setWalletData] = useState({
    addresses: [],
    wallet: {},
    txs: [],
  });
  const [txData, setTxData] = useState({});
  const [btcPrice, setBtcPrice] = useState(0);
  const [sanctioned, setSanctioned] = useState(undefined);

  // The sanctions list is 17KB of addresses that only this page and the list
  // page need, so it is fetched as its own chunk rather than carried in the
  // bundle every visitor downloads. A match is rare, and arriving a moment late
  // costs nothing - the banner appears above content that is still loading
  // anyway.
  useEffect(() => {
    if (kind !== "wallet") return setSanctioned(undefined);
    let cancelled = false;
    import("./sanctionedAddresses")
      .then(({ findSanctioned }) => {
        if (!cancelled) setSanctioned(findSanctioned(walletId));
      })
      .catch((error) => {
        // a missing banner is not worth breaking the lookup over
        console.error("Sanctions list failed to load:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [walletId, kind]);

  useEffect(() => {
    if (!kind) return;
    setIsLoading(true);
    const isTxResult = kind === "transaction";

    (async () => {
      try {
        if (isTxResult) {
          setTxData(await getTxData(walletId));
        } else {
          setWalletData(await getWalletData(walletId));
        }
        setBtcPrice(await getBtcPrice());
      } catch (error) {
        console.error("Explorer lookup failed:", error);
        const failed = {
          error: "Something went wrong loading this lookup",
          wallet: [],
          fetched: true,
        };
        if (isTxResult) setTxData(failed);
        else setWalletData(failed);
      } finally {
        // must always run, or the loading bar sits at 100% forever
        setIsLoading(false);
      }
    })();
  }, [walletId, kind]);

  const {
    addresses,
    wallet: { final_balance },
    txs,
  } = walletData;
  const btcBalanceNumber = satsToBtc(final_balance);
  const btcBalancePretty = btcToString(btcBalanceNumber);
  const balanceUsdPretty = fmtUsd(btcPrice * btcBalanceNumber);
  const btcPricePretty = fmtUsd(btcPrice);

  const lookupKind = isTx ? "transaction" : "wallet";

  // Every address this page is looking up. Taken from the response rather than
  // the URL segment, because multiaddr answers pipe-delimited lookups with all
  // of them and the segment is then "addr1|addr2", matching no address at all -
  // which would list half of a two-address lookup as the other side of its own
  // transactions.
  //
  // No page reaches that today: verifyId rejects a pipe-delimited id, so those
  // URLs 404 before this runs, even though BtcSearch still offers the syntax
  // and still navigates to them. That is its own bug. Reading the response is
  // the correct thing either way, and costs nothing while the path is dead.
  const ownAddresses = new Set(
    addresses?.length ? addresses.map((a) => a.address) : [walletId]
  );

  // Newest first.
  //
  // blockchain.info returns these roughly in that order already, but sorted by
  // block height while the timestamp it reports is when the transaction was
  // first relayed, not when it was mined. A transaction broadcast early and
  // confirmed late lands in an earlier block than one broadcast after it, so
  // adjacent rows can show times running backwards - which reads as an
  // unordered list even though the chain order is right.
  //
  // Sorting by block height keeps the chain order and makes it deliberate.
  // Anything unconfirmed has no height yet and belongs at the top, since it is
  // more recent than everything that has already been mined.
  const orderedTxs = [...(txs ?? [])].sort((a, b) => {
    const heightA = a.block_height ?? Infinity;
    const heightB = b.block_height ?? Infinity;
    if (heightA !== heightB) return heightB - heightA;
    return b.time - a.time;
  });
  // a known wallet can say whose it is, rather than just quoting the address
  const curated = isTx ? undefined : findCuratedWallet(walletId);
  // one source for the heading, the title and the link preview - the edge
  // function builds the served HTML from these same two functions
  const meta = curated
    ? walletMeta(curated, BALANCES_CHECKED)
    : lookupMeta(lookupKind, walletId);
  const pool = isTx ? undefined : findMiningPool(walletId);

  // An id that cannot be real gets the same page any other missing URL gets,
  // rather than an empty lookup wearing its heading.
  if (!kind) {
    return (
      <NotFound
        note={
          idShapeOf(walletId) === "wallet"
            ? "That is the right shape for a Bitcoin address, but its checksum does not match, so there is a typo in it somewhere. Every address carries a checksum for exactly this reason - a mistyped one is not somebody else's wallet."
            : "That is not a Bitcoin address or a transaction hash. An address starts with 1, 3 or bc1; a transaction hash is 64 hexadecimal characters."
        }
      />
    );
  }

  return (
    <BtcExplorerPage>
      <Seo {...meta} path={`/btc-explorer/${walletId}`} />
      {/* a known wallet is headed with its label; a lookup introduces the
          address list below it */}
      <Title>
        {meta.heading}
        {!curated && ":"}
      </Title>
      {pool && (
        <PoolBanner>
          <PoolLabel>Mining pool</PoolLabel>
          <PoolName>{pool.pool}</PoolName>
          <PoolMeta>
            This address received the block reward {pool.blocks} times in the{" "}
            {MINING_POOLS_SAMPLE.toLocaleString()} blocks sampled on{" "}
            {MINING_POOLS_AS_OF} — {pool.share}% of them. Worked out from the
            chain, not attributed; see{" "}
            <Link to="/btc-explorer/mining-pools">all pool addresses</Link>.
          </PoolMeta>
        </PoolBanner>
      )}
      {sanctioned && (
        <Sanctioned>
          <SanctionedLabel>OFAC sanctioned</SanctionedLabel>
          <SanctionedName>{sanctioned.name}</SanctionedName>
          <SanctionedMeta>
            Designated by the US Treasury{" "}
            {sanctioned.programs.length > 0 &&
              `under ${sanctioned.programs.join(", ")} `}
            and listed on its Specially Designated Nationals list. Taken from{" "}
            <a href={SANCTIONS_SOURCE} target="_blank" rel="noreferrer noopener">
              the SDN list
            </a>{" "}
            on {SANCTIONS_AS_OF}; see{" "}
            <Link to="/btc-explorer/sanctioned">all sanctioned addresses</Link>.
          </SanctionedMeta>
        </Sanctioned>
      )}
      {curated && (
        <KnownWallet>
          <KnownCategory>{curated.category}</KnownCategory>
          <KnownNote>{meta.intro}</KnownNote>
          <KnownMeta>
            {meta.provenance.before}
            <Link to={meta.provenance.link.href}>
              {meta.provenance.link.text}
            </Link>
            {meta.provenance.after}
          </KnownMeta>
          <KnownCaveat>{meta.caveat}</KnownCaveat>
        </KnownWallet>
      )}
      {/* TODO: search bar to the right of Title header */}

      {isLoading ? (
        <LoadingWrapper>
          <LoadingBar />
        </LoadingWrapper>
      ) : isTx ? (
        <>
          {txData.error ? (
            <Empty>
              {txData.error === "No results"
                ? "No transaction with this hash has reached the explorer. A hash is only known once the transaction has been broadcast."
                : txData.error}
            </Empty>
          ) : (
            <FullTxDetails txData={txData} btcPrice={btcPrice} />
          )}
        </>
      ) : (
        <>
          {walletData.error ? (
            <Empty>
              {walletData.error === "No results"
                ? "This address is valid but the explorer has nothing for it: no transactions, and so no balance. An address exists whether or not it has ever been used."
                : walletData.error}
            </Empty>
          ) : (
            <>
              <AddressList>
                {addresses.map((x) => (
                  <li key={x.address}>{x.address}</li>
                ))}
              </AddressList>

              <BalanceHeader>Balance:</BalanceHeader>
              <BtcBalPara>{btcBalancePretty} BTC</BtcBalPara>
              <UsdBalPara>${balanceUsdPretty}</UsdBalPara>
              <p>Spot price: ${btcPricePretty}/USD</p>

              {/* <Col xs={12} sm={12} md={7} lg={7}> */}
              <div className="terminal-timeline">
                {orderedTxs.map((x) => {
                  const fmtBtc = Math.abs(satsToBtc(x.result));
                  const fmtBtcPretty = btcToString(fmtBtc);
                  const isOutgoing = x.result < 0;
                  return (
                    // TODO: make this own component
                    <TerminalCard key={x.hash}>
                      <div className="terminal-card">
                        <header>
                          TX:{" "}
                          <TxLink to={`/btc-explorer/${x.hash}`}>
                            {truncateHash(x.hash)}
                          </TxLink>
                        </header>
                        <div>
                          <TxDetails
                            style={{ color: isOutgoing ? "red" : "green" }}
                          >
                            {fmtBtcPretty} BTC (${fmtUsd(btcPrice * fmtBtc)}){" "}
                            {isOutgoing ? "sent" : "received"}
                          </TxDetails>
                          <Counterparties
                            tx={x}
                            own={ownAddresses}
                            incoming={!isOutgoing}
                          />
                          <TxDetails>
                            {new Date(x.time * 1000).toLocaleString()}{" "}
                            <BlockRef>
                              {x.block_height
                                ? `· block ${x.block_height.toLocaleString()}`
                                : "· unconfirmed"}
                            </BlockRef>
                          </TxDetails>
                        </div>
                      </div>
                    </TerminalCard>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </BtcExplorerPage>
  );
};

export default BtcExplorer;
